import React from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Chip, Stack, Tooltip } from "@mui/material";

import { EasyWinChip } from "../EasyWin/EasyWin";
import { type Insight } from "../types";

const providerDisplayNames = {
  "aws-trusted-advisor": "Trusted Advisor",
  "aws-cost-optimization-hub": "Cost Optimization Hub",
  "gcp-recommender": "Google Cloud Recommender",
};

const getStatusBadge = (insight: Insight): React.ReactNode => {
  switch (insight.displayStatus) {
    case "permissions needed":
      return (
        <Chip
          variant="status"
          label="Permissions needed"
          color="warning"
          data-cy="permissions-needed-label"
          sx={{ ml: 1, mb: 0.3 }}
          icon={
            <Tooltip
              title={`Enable ${providerDisplayNames[insight.providerId] ?? "these"} insights on your accounts to get detailed recommendations, savings estimates and more.`}
              arrow
            >
              <InfoOutlinedIcon />
            </Tooltip>
          }
        />
      );
    case "dismissed":
      return <Chip label="Dismissed" variant="status" size="small" sx={{ ml: 1, mb: 0.3 }} />;
    case "optimized":
      return <Chip label="Optimized" variant="status" size="small" color="success" sx={{ ml: 1, mb: 0.3 }} />;
    case "actionable":
      return <Chip label="Actionable" variant="status" size="small" color="primary" sx={{ ml: 1, mb: 0.3 }} />;
    default:
      return null;
  }
};

export const getPriorityBadge = (insight: Insight): React.ReactNode => {
  switch (insight.priority) {
    case "High":
      return <Chip label="High priority" variant="status" size="small" color="error" sx={{ ml: 1, mb: 0.3 }} />;
    case "Medium":
      return <Chip label="Medium priority" variant="status" size="small" color="info" sx={{ ml: 1, mb: 0.3 }} />;
    case "Low":
      return <Chip label="Low priority" variant="status" size="small" sx={{ ml: 1, mb: 0.3 }} />;
    default:
      return null;
  }
};

const getIsInternalBadge = (isInternal: boolean): React.ReactNode => {
  if (isInternal) {
    return <Chip label="DoiT internal" color="primary" variant="status" sx={{ ml: 1, mb: 0.3 }} />;
  } else {
    return null;
  }
};

const getEasyWinBadge = (insight: Insight): React.ReactNode => {
  if (insight.easyWinDescription) {
    return <EasyWinChip tooltipText={insight.easyWinDescription} />;
  } else {
    return null;
  }
};

type InsightBadgesProps = {
  insight: Insight;
};

const InsightBadges: React.FC<InsightBadgesProps> = ({ insight }) => {
  const badges: React.ReactNode[] = [
    getStatusBadge(insight),
    getPriorityBadge(insight),
    getIsInternalBadge(insight.isInternal),
    getEasyWinBadge(insight),
  ].filter(Boolean);

  if (badges.length === 0) {
    return null;
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ color: "text.secondary", mt: 0.5, mb: 0.5 }}
      height="28px"
    >
      {badges.map((badge, index) => (
        <Stack key={index} direction="row" spacing={0.75} alignItems="center">
          {badge}
        </Stack>
      ))}
    </Stack>
  );
};

export default InsightBadges;
